import { css } from '@emotion/react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { CheckboxComponent } from '../../baseComponents/checkbox';

type DisclaimerProps = {
  className?: string;
  checkboxLabel?: string;
  onClose: (isPermanent: boolean) => void;
  children?: React.ReactNode;
};

const closeButtonStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
  background: 'none',
  border: 'none',
  position: 'absolute',
  cursor: 'pointer',
  top: 0,
  right: 0,
  fontSize: 20,
  height: 34,
  width: 34,
  lineHeight: '34px',
  textAlign: 'center',
  outline: 'none',
});

const wrapperStyle = (theme: Theme) => css({
  position: 'relative',
  padding: '23px 23px 13px',
  borderRadius: 4,
  background: theme.backgroundColors.secondary,
  border: '1px solid ' + theme.lineColors.basePrimary,
  boxShadow: `0 0 0 2px ${theme.buttonColors.secondaryBackground}`,
  marginTop: 2, // Makes the shadow included in the parent layout-wise

  '&:first-of-type': {
    marginLeft: 2, // Makes the shadow included in the parent layout-wise
  },
});

const checkboxWrapperStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  paddingTop: 14,
});

const disclaimerContentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 250,
  minHeight: 0,
});

export const DisclaimerComponent: FC<DisclaimerProps> = (props) => {
  const [isClosePermanent, setIsClosePermanent] = useState(false);

  const onConfirmPermanentCheckboxClick = () => {
    setIsClosePermanent(isPermanent => !isPermanent);
  };

  return (
    <div
      css={wrapperStyle}
      className={props.className}
    >
      <button
        css={closeButtonStyle}
        onClick={() => props.onClose(isClosePermanent)}
        aria-label="Close disclaimer panel"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>

      <OverlayScrollbarComponent
        type={ScrollbarType.Vertical}
        css={disclaimerContentStyle}
        showScrollFade
      >
        {props.children}
      </OverlayScrollbarComponent>

      {props.checkboxLabel && (
        <div css={checkboxWrapperStyle}>
          <CheckboxComponent
            isChecked={isClosePermanent}
            checkedSetter={onConfirmPermanentCheckboxClick}
            text={props.checkboxLabel}
          />
        </div>
      )}
    </div>
  );
};
